// src/firebase/services.js
// ─────────────────────────────────────────────────────────
// OPTIMIZED for 300K users:
// 1. In-memory caching — avoids repeated Firestore reads
// 2. Pagination — loads 20 items at a time, not everything
// 3. Efficient queries — uses indexes, limits, and batching
// ─────────────────────────────────────────────────────────

import {
  collection, doc, addDoc, updateDoc, getDoc,
  getDocs, query, where, orderBy, limit,
  startAfter, serverTimestamp, getCountFromServer,
} from "firebase/firestore";
import { ref, uploadBytesResumable, getDownloadURL } from "firebase/storage";
import { db, storage } from "./config";

// ═══════════════════════════════════════════════════════════
// OPTIMIZATION 1: IN-MEMORY CACHE
// ═══════════════════════════════════════════════════════════
// Instead of hitting Firestore every time, we store results
// in memory for a set TTL (time to live).
// A member profile cached for 5 minutes = 1 read instead of
// potentially 100+ reads if they navigate around the dashboard.

const cache = new Map();
const CACHE_TTL = {
  profile:       5 * 60 * 1000,  // 5 minutes
  applications:  2 * 60 * 1000,  // 2 minutes
  notifications: 1 * 60 * 1000,  // 1 minute
  adminStats:    5 * 60 * 1000,  // 5 minutes
};

function getCached(key) {
  const entry = cache.get(key);
  if (!entry) return null;
  if (Date.now() - entry.timestamp > entry.ttl) {
    cache.delete(key);
    return null;
  }
  return entry.data;
}

function setCached(key, data, ttl) {
  cache.set(key, { data, timestamp: Date.now(), ttl });
}

function invalidateCache(key) {
  cache.delete(key);
}

function invalidateCachePrefix(prefix) {
  for (const key of cache.keys()) {
    if (key.startsWith(prefix)) cache.delete(key);
  }
}

// ═══════════════════════════════════════════════════════════
// MEMBER PROFILE
// ═══════════════════════════════════════════════════════════

export async function getMemberProfile(uid) {
  const cacheKey = `profile:${uid}`;
  const cached = getCached(cacheKey);
  if (cached) return cached; // Return cached — no Firestore read!

  const snap = await getDoc(doc(db, "members", uid));
  const data = snap.exists() ? snap.data() : null;
  if (data) setCached(cacheKey, data, CACHE_TTL.profile);
  return data;
}

export async function updateMemberProfile(uid, data) {
  await updateDoc(doc(db, "members", uid), {
    ...data,
    updatedAt: serverTimestamp(),
  });
  invalidateCache(`profile:${uid}`); // Clear cache so next fetch is fresh
}

export async function confirmMembershipPayment(uid, paymentRef) {
  await updateDoc(doc(db, "members", uid), {
    hasPaidMembership: true,
    membershipAmount:  250,
    membershipDate:    new Date().toLocaleDateString("en-GB", {
      day: "2-digit", month: "short", year: "numeric",
    }),
    membershipRef:    paymentRef,
    membershipPaidAt: serverTimestamp(),
  });
  invalidateCache(`profile:${uid}`); // Force fresh fetch after payment
}

// ═══════════════════════════════════════════════════════════
// OPTIMIZATION 2: PAGINATION
// ═══════════════════════════════════════════════════════════
// Instead of loading ALL applications at once (dangerous at 300K users),
// we load PAGE_SIZE at a time and provide a cursor for next page.
// This means each page load costs 20 reads instead of potentially thousands.

const PAGE_SIZE = 20;

/**
 * Get member applications with pagination
 * @param {string} uid - User ID
 * @param {object|null} lastDoc - Last document from previous page (cursor)
 * @returns {{ applications: array, lastDoc: object|null, hasMore: boolean }}
 */
export async function getMemberApplicationsPaginated(uid, lastDoc = null) {
  const cacheKey = `apps:${uid}:${lastDoc?.id || "first"}`;
  const cached = getCached(cacheKey);
  if (cached) return cached;

  // Build paginated query
  let q = query(
    collection(db, "applications"),
    where("uid", "==", uid),
    orderBy("submittedAt", "desc"),
    limit(PAGE_SIZE)
  );

  // If we have a cursor, start after it
  if (lastDoc) {
    q = query(
      collection(db, "applications"),
      where("uid", "==", uid),
      orderBy("submittedAt", "desc"),
      startAfter(lastDoc),
      limit(PAGE_SIZE)
    );
  }

  const snap = await getDocs(q);
  const applications = snap.docs.map(d => ({ id: d.id, ...d.data() }));
  const newLastDoc   = snap.docs[snap.docs.length - 1] || null;
  const hasMore      = snap.docs.length === PAGE_SIZE;

  const result = { applications, lastDoc: newLastDoc, hasMore };
  setCached(cacheKey, result, CACHE_TTL.applications);
  return result;
}

/**
 * Get ALL member applications (non-paginated — use only when needed)
 * Cached to avoid repeated reads
 */
export async function getMemberApplications(uid) {
  const cacheKey = `apps:all:${uid}`;
  const cached = getCached(cacheKey);
  if (cached) return cached;

  const q = query(
    collection(db, "applications"),
    where("uid", "==", uid),
    orderBy("submittedAt", "desc"),
    limit(50) // Safety limit — no user should have more than 50 applications
  );
  const snap = await getDocs(q);
  const data = snap.docs.map(d => ({ id: d.id, ...d.data() }));
  setCached(cacheKey, data, CACHE_TTL.applications);
  return data;
}

export async function getApplication(appId) {
  const cacheKey = `app:${appId}`;
  const cached = getCached(cacheKey);
  if (cached) return cached;

  const snap = await getDoc(doc(db, "applications", appId));
  const data = snap.exists() ? { id: snap.id, ...snap.data() } : null;
  if (data) setCached(cacheKey, data, CACHE_TTL.applications);
  return data;
}

// ═══════════════════════════════════════════════════════════
// GRANT APPLICATION SUBMISSION
// ═══════════════════════════════════════════════════════════

export async function submitGrantApplication(uid, formData, fileUrls = []) {
  const docRef = await addDoc(collection(db, "applications"), {
    uid,
    ...formData,
    fileUrls,
    status:          "Under Review",
    submittedAt:     serverTimestamp(),
    updatedAt:       serverTimestamp(),
    adminNotes:      "",
    disbursedAt:     null,
    disbursedAmount: 0,
  });
  // Invalidate application cache so dashboard refreshes
  invalidateCachePrefix(`apps:${uid}`);
  return docRef.id;
}

// ═══════════════════════════════════════════════════════════
// ADMIN — APPLICATION MANAGEMENT
// ═══════════════════════════════════════════════════════════

/**
 * Get all applications with pagination for admin
 * @param {object|null} lastDoc - Cursor for pagination
 * @param {string} statusFilter - Filter by status or "all"
 */
export async function getAllApplicationsPaginated(lastDoc = null, statusFilter = "all") {
  let q;

  if (statusFilter === "all") {
    q = lastDoc
      ? query(collection(db, "applications"), orderBy("submittedAt", "desc"), startAfter(lastDoc), limit(PAGE_SIZE))
      : query(collection(db, "applications"), orderBy("submittedAt", "desc"), limit(PAGE_SIZE));
  } else {
    q = lastDoc
      ? query(collection(db, "applications"), where("status", "==", statusFilter), orderBy("submittedAt", "desc"), startAfter(lastDoc), limit(PAGE_SIZE))
      : query(collection(db, "applications"), where("status", "==", statusFilter), orderBy("submittedAt", "desc"), limit(PAGE_SIZE));
  }

  const snap = await getDocs(q);
  return {
    applications: snap.docs.map(d => ({ id: d.id, ...d.data() })),
    lastDoc:      snap.docs[snap.docs.length - 1] || null,
    hasMore:      snap.docs.length === PAGE_SIZE,
  };
}

// Keep backward-compatible version for admin dashboard
export async function getAllApplications() {
  const q = query(
    collection(db, "applications"),
    orderBy("submittedAt", "desc"),
    limit(100) // Limit to 100 most recent for admin overview
  );
  const snap = await getDocs(q);
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
}

export async function approveApplication(appId, uid, amount) {
  await updateDoc(doc(db, "applications", appId), {
    status:    "Approved",
    updatedAt: serverTimestamp(),
  });
  invalidateCache(`app:${appId}`);
  invalidateCachePrefix(`apps:${uid}`);
  await addNotification(uid, {
    icon: "✅",
    text: `Your grant application has been approved! Amount: ₦${Number(amount).toLocaleString()}`,
  });
}

export async function rejectApplication(appId, uid, reason = "") {
  await updateDoc(doc(db, "applications", appId), {
    status:     "Rejected",
    adminNotes: reason,
    updatedAt:  serverTimestamp(),
  });
  invalidateCache(`app:${appId}`);
  invalidateCachePrefix(`apps:${uid}`);
  await addNotification(uid, {
    icon: "❌",
    text: `Your grant application was not approved.${reason ? " Reason: " + reason : ""}`,
  });
}

export async function disburseGrant(appId, uid, amount) {
  await updateDoc(doc(db, "applications", appId), {
    status:          "Disbursed",
    disbursedAt:     serverTimestamp(),
    disbursedAmount: amount,
    updatedAt:       serverTimestamp(),
  });
  invalidateCache(`app:${appId}`);
  invalidateCachePrefix(`apps:${uid}`);
  await addNotification(uid, {
    icon: "💰",
    text: `₦${Number(amount).toLocaleString()} has been disbursed to your account!`,
  });
}

// ═══════════════════════════════════════════════════════════
// NOTIFICATIONS
// ═══════════════════════════════════════════════════════════

export async function addNotification(uid, { icon, text }) {
  await addDoc(collection(db, "members", uid, "notifications"), {
    icon,
    text,
    unread:    true,
    createdAt: serverTimestamp(),
  });
  invalidateCachePrefix(`notifs:${uid}`);
}

export async function getMemberNotifications(uid) {
  const cacheKey = `notifs:${uid}`;
  const cached = getCached(cacheKey);
  if (cached) return cached;

  const q = query(
    collection(db, "members", uid, "notifications"),
    orderBy("createdAt", "desc"),
    limit(20) // Only fetch latest 20 notifications
  );
  const snap = await getDocs(q);
  const data = snap.docs.map(d => ({ id: d.id, ...d.data() }));
  setCached(cacheKey, data, CACHE_TTL.notifications);
  return data;
}

export async function markNotificationRead(uid, notifId) {
  await updateDoc(doc(db, "members", uid, "notifications", notifId), {
    unread: false,
  });
  invalidateCachePrefix(`notifs:${uid}`);
}

export async function markAllNotificationsRead(uid) {
  const notifs = await getMemberNotifications(uid);
  const unread = notifs.filter(n => n.unread);
  await Promise.all(
    unread.map(n =>
      updateDoc(doc(db, "members", uid, "notifications", n.id), { unread: false })
    )
  );
  invalidateCachePrefix(`notifs:${uid}`);
}

// ═══════════════════════════════════════════════════════════
// FILE UPLOAD
// ═══════════════════════════════════════════════════════════

export function uploadFile(file, uid, onProgress) {
  return new Promise((resolve, reject) => {
    const path    = `applications/${uid}/${Date.now()}_${file.name}`;
    const fileRef = ref(storage, path);
    const task    = uploadBytesResumable(fileRef, file);
    task.on(
      "state_changed",
      snap => {
        const pct = Math.round((snap.bytesTransferred / snap.totalBytes) * 100);
        if (onProgress) onProgress(pct);
      },
      reject,
      async () => {
        const url = await getDownloadURL(task.snapshot.ref);
        resolve({ url, name: file.name, path });
      }
    );
  });
}

// ═══════════════════════════════════════════════════════════
// OPTIMIZATION 3: EFFICIENT ADMIN STATS
// ═══════════════════════════════════════════════════════════
// Instead of downloading ALL documents to count them,
// we use getCountFromServer() which only costs 1 read
// regardless of how many documents exist.
// For 300K members, old way = 300K reads. New way = 1 read.

export async function getAdminStats() {
  const cacheKey = "adminStats";
  const cached = getCached(cacheKey);
  if (cached) return cached;

  try {
    // Use count queries — much cheaper than fetching all documents
    const [
      totalMembersCount,
      paidMembersCount,
      totalAppsCount,
      underReviewCount,
      approvedCount,
      rejectedCount,
      disbursedCount,
    ] = await Promise.all([
      getCountFromServer(collection(db, "members")),
      getCountFromServer(query(collection(db, "members"), where("hasPaidMembership", "==", true))),
      getCountFromServer(collection(db, "applications")),
      getCountFromServer(query(collection(db, "applications"), where("status", "==", "Under Review"))),
      getCountFromServer(query(collection(db, "applications"), where("status", "==", "Approved"))),
      getCountFromServer(query(collection(db, "applications"), where("status", "==", "Rejected"))),
      getCountFromServer(query(collection(db, "applications"), where("status", "==", "Disbursed"))),
    ]);

    // We still need to sum disbursed amounts — fetch only disbursed docs
    const disbursedSnap = await getDocs(
      query(collection(db, "applications"), where("status", "==", "Disbursed"), limit(500))
    );
    const totalDisbursed = disbursedSnap.docs.reduce(
      (sum, d) => sum + (d.data().disbursedAmount || 0), 0
    );

    const paidCount = paidMembersCount.data().count;

    const stats = {
      totalMembers:      totalMembersCount.data().count,
      paidMembers:       paidCount,
      unpaidMembers:     totalMembersCount.data().count - paidCount,
      totalApplications: totalAppsCount.data().count,
      underReview:       underReviewCount.data().count,
      approved:          approvedCount.data().count,
      rejected:          rejectedCount.data().count,
      disbursedCount:    disbursedCount.data().count,
      totalDisbursed,
      membershipRevenue: paidCount * 250,
    };

    setCached(cacheKey, stats, CACHE_TTL.adminStats);
    return stats;
  } catch (err) {
    // Fallback for older Firebase SDK versions that don't support getCountFromServer
    console.warn("Count queries not supported, falling back to full fetch:", err);
    const [membersSnap, appsSnap] = await Promise.all([
      getDocs(collection(db, "members")),
      getDocs(collection(db, "applications")),
    ]);
    const members      = membersSnap.docs.map(d => d.data());
    const applications = appsSnap.docs.map(d => d.data());
    const paidMembers  = members.filter(m => m.hasPaidMembership).length;
    const disbursed    = applications.filter(a => a.status === "Disbursed");
    return {
      totalMembers:      members.length,
      paidMembers,
      unpaidMembers:     members.length - paidMembers,
      totalApplications: applications.length,
      approved:          applications.filter(a => a.status === "Approved").length,
      rejected:          applications.filter(a => a.status === "Rejected").length,
      underReview:       applications.filter(a => a.status === "Under Review").length,
      totalDisbursed:    disbursed.reduce((s, a) => s + (a.disbursedAmount || 0), 0),
      disbursedCount:    disbursed.length,
      membershipRevenue: paidMembers * 250,
    };
  }
}

// ═══════════════════════════════════════════════════════════
// CACHE UTILITIES — exposed for manual cache management
// ═══════════════════════════════════════════════════════════

export function clearUserCache(uid) {
  invalidateCachePrefix(`profile:${uid}`);
  invalidateCachePrefix(`apps:${uid}`);
  invalidateCachePrefix(`notifs:${uid}`);
}

export function clearAllCache() {
  cache.clear();
}
